// src/services/error/errorHandlingService.js

/**
 * Centralized error handling service for the application
 * Provides error reporting, logging, and notification capabilities
 */
class ErrorHandlingService {
  constructor() {
    this.listeners = [];
    this.errorHistory = [];
    this.maxErrorHistory = 50; // Maximum number of errors to store
    this.initialized = false;
  }

  /**
   * Initialize error handling service with global error handlers
   */
  init() {
    if (this.initialized) return;
    
    this.initialized = true;
    console.log('Error handling service initialized');
  }

  /**
   * Report an error to the error handling service
   * @param {Error|Object|string} error - The error to report
   * @param {Object} metadata - Additional metadata about the error
   */
  reportError(error, metadata = {}) {
    // Create a standardized error object
    const errorObject = {
      timestamp: new Date(),
      error: error instanceof Error ? error : new Error(String(error)),
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      metadata: {
        ...metadata,
        url: window.location.href,
        userAgent: navigator.userAgent,
      }
    };

    // Add to error history
    this.addToErrorHistory(errorObject);

    // Notify all listeners
    this.notifyListeners(errorObject);

    // Return the error object for chaining
    return errorObject;
  }

  /**
   * Add a listener to be notified when errors occur
   * @param {Function} listener - Function to call when an error occurs
   * @returns {Function} - Function to remove the listener
   */
  addListener(listener) {
    if (typeof listener !== 'function') {
      console.warn('Invalid listener added to error handling service');
      return () => {};
    }

    this.listeners.push(listener);
    
    // Return function to remove this listener
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Add error to the error history
   * @param {Object} errorObject - Standardized error object
   */
  addToErrorHistory(errorObject) {
    this.errorHistory.unshift(errorObject);
    
    // Trim history if it exceeds maximum size
    if (this.errorHistory.length > this.maxErrorHistory) {
      this.errorHistory = this.errorHistory.slice(0, this.maxErrorHistory);
    }
  }

  /**
   * Notify all listeners about an error
   * @param {Object} errorObject - Standardized error object
   */
  notifyListeners(errorObject) {
    this.listeners.forEach(listener => {
      try {
        listener(errorObject);
      } catch (err) {
        console.error('Error in error listener:', err);
      }
    });
  }

  /**
   * Get recent errors from the error history
   * @param {number} limit - Maximum number of errors to return
   * @returns {Array} - Array of recent errors
   */
  getRecentErrors(limit = 10) {
    return this.errorHistory.slice(0, limit);
  }

  /**
   * Clear the error history
   */
  clearErrorHistory() {
    this.errorHistory = [];
  }
}

// Create a singleton instance
const errorHandlingService = new ErrorHandlingService();

// Initialize service
errorHandlingService.init();

export default errorHandlingService;